// MonacoEditor.js
// Monaco-based code editor component for Zolaid IDE (with fallback on load error)
import React, { useRef, useEffect, useState } from 'react';

const MonacoEditor = ({ value, language = 'javascript', onChange, theme = 'vs-dark', minimap = true }) => {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const editorInstance = useRef(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let disposed = false;
    let loaded = false;
    setLoadError(null);
    const timeout = window.setTimeout(() => {
      if (disposed || loaded) return;
      setLoadError('Monaco took too long to load');
    }, 12000);
    (async () => {
      try {
        if (!window.monaco) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/monaco-editor@0.44.0/min/vs/loader.js';
            script.onload = resolve;
            script.onerror = () => reject(new Error('Monaco loader failed to load'));
            document.body.appendChild(script);
          });
        }
        if (disposed || !editorRef.current) return;
        if (typeof window.require !== 'function') {
          setLoadError('Monaco require not available');
          return;
        }
        window.require.config({ paths: { vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.44.0/min/vs' } });
        window.require(['vs/editor/editor.main'], (monaco) => {
          if (disposed || !editorRef.current) return;
          loaded = true;
          try {
            monacoRef.current = monaco;
            editorInstance.current = monaco.editor.create(editorRef.current, {
              value: value ?? '',
              language,
              theme,
              automaticLayout: true,
              minimap: { enabled: minimap },
            });
            editorInstance.current.onDidChangeModelContent(() => {
              onChange && onChange(editorInstance.current.getValue());
            });
            editorInstance.current.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
              window.dispatchEvent(new CustomEvent('editor-save'));
            });
            editorInstance.current.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyP, () => {
              window.dispatchEvent(new CustomEvent('editor-command-palette'));
            });
          } catch (err) {
            setLoadError(err?.message || 'Monaco init failed');
          }
        });
      } catch (err) {
        setLoadError(err?.message || 'Monaco failed to load');
      }
    })();
    return () => {
      disposed = true;
      loaded = true;
      window.clearTimeout(timeout);
      if (editorInstance.current) {
        try {
          editorInstance.current.dispose();
        } catch (_) {}
        editorInstance.current = null;
      }
    };
  }, [theme, minimap]);

  useEffect(() => {
    if (loadError) return;
    if (monacoRef.current && editorInstance.current) {
      try {
        const model = editorInstance.current.getModel();
        if (model && value !== model.getValue()) {
          model.setValue(value ?? '');
        }
        monacoRef.current.editor.setTheme(theme);
      } catch (_) {}
    }
  }, [value, theme, loadError]);

  if (loadError) {
    return (
      <pre
        style={{
          width: '100%',
          height: '100%',
          margin: 0,
          padding: 12,
          overflow: 'auto',
          fontSize: 13,
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          background: '#1e1e1e',
          color: '#d4d4d4',
          border: '1px solid #333',
        }}
      >
        {value ?? '(no code)'}
      </pre>
    );
  }

  return <div ref={editorRef} style={{ width: '100%', height: '100%' }} />;
};

export default MonacoEditor;
